// src/utils/appointmentPayment.js
//
// 16 Jul 2026 — new. Same orchestration as labTestPayment.js, for doctor
// appointments: create a gateway order (server resolves the flat ₹250
// price via AppointmentPricingService — see that file for the future
// per-doctor/visit-type pricing seam) → open Razorpay's standard Checkout
// modal → on success, verify server-side and only THEN create the actual
// appointment. Kept as a separate file from labTestPayment.js rather than
// generalised into one shared "payForResource" function — the two flows'
// order-payload shapes are different enough (CreateOrderRequest vs
// AppointmentDTO.CreateRequest) that forcing them through one function
// would need a bunch of resource-type branching inside it; two small,
// obvious files are clearer than one with a type parameter threading
// through everything.
//
// 27 Jul 2026 — added payAndCreateAppointmentWithWallet below, mirroring
// labTestPayment.js's payAndCreateLabTestOrderWithWallet exactly.

import { paymentAPI } from '../services/api';
import { openRazorpayCheckout } from '../services/razorpay';

/**
 * @param {object} params
 * @param {string} params.patientId
 * @param {string} params.doctorId
 * @param {string} [params.appointmentType] FOLLOW_UP / NEW_CONSULTATION / ROUTINE / EMERGENCY
 * @param {string} [params.doctorName]      shown on the Razorpay modal
 * @param {string} [params.patientName]
 * @param {string} [params.patientEmail]
 * @param {string} [params.patientPhone]
 * @param {object} params.appointmentPayload the full AppointmentDTO.CreateRequest-shaped
 *                                            body (same shape already sent to
 *                                            /appointments directly before this change)
 * @param {string} [params.paymentMode]      defaults 'RAZORPAY_UPFRONT' — see
 *                                            PayAndCreateAppointmentRequest.java's
 *                                            Javadoc for the SUBSCRIPTION_COVERED seam.
 * @param {string} [params.countryCode]
 * @param {string} [params.subscriptionPlanId]
 * @returns {Promise<{success: true, appointment: object} |
 *                    {success: false, reason: 'payment_cancelled'|'payment_failed'|'appointment_creation_failed'|'create_order_failed', message?: string}>}
 */
export async function payAndCreateAppointment({
  patientId, doctorId, appointmentType, doctorName, patientName, patientEmail, patientPhone,
  appointmentPayload, paymentMode = 'RAZORPAY_UPFRONT', countryCode, subscriptionPlanId,
}) {
  // Step 1 — create the gateway order (server resolves the amount)
  let razorpayOrderId, amount, currency;
  try {
    const createRes = await paymentAPI.createAppointmentPaymentOrder({
      patientId, doctorId, appointmentType, countryCode, subscriptionPlanId,
    });
    razorpayOrderId = createRes.data.razorpayOrderId;
    amount           = createRes.data.amount;
    currency         = createRes.data.currency;
  } catch (err) {
    return {
      success: false,
      reason: 'create_order_failed',
      message: err?.response?.data?.error || err.message || 'Could not start the payment.',
    };
  }

  // Step 2 — open Razorpay's standard Checkout modal
  let checkoutResult;
  try {
    checkoutResult = await openRazorpayCheckout({
      orderId: razorpayOrderId,
      amount,
      currency,
      name: 'Previu',
      description: doctorName ? `Consultation with ${doctorName}` : 'Doctor consultation',
      prefill: {
        name: patientName || '',
        email: patientEmail || '',
        contact: patientPhone || '',
      },
      themeColor: '#b8843a',
    });
  } catch (err) {
    return { success: false, reason: 'payment_failed', message: err.message };
  }

  if (!checkoutResult) {
    return { success: false, reason: 'payment_cancelled' };
  }

  // Step 3 — verify signature server-side, THEN create the appointment
  try {
    const res = await paymentAPI.payAndCreateAppointment({
      razorpayOrderId:   checkoutResult.razorpay_order_id,
      razorpayPaymentId: checkoutResult.razorpay_payment_id,
      razorpaySignature: checkoutResult.razorpay_signature,
      appointment: appointmentPayload,
      paymentMode,
      countryCode,
      subscriptionPlanId,
    });
    return { success: true, appointment: res.data };
  } catch (err) {
    // Payment DID succeed here — same "do not invite retry-by-paying-again"
    // reasoning as labTestPayment.js's identical case.
    return {
      success: false,
      reason: 'appointment_creation_failed',
      message: err?.response?.data?.message || err.message ||
        'Payment succeeded but the appointment could not be created. Please contact support — do not pay again.',
    };
  }
}

/**
 * 27 Jul 2026 — new. WALLET_BALANCE mode — mirrors
 * payAndCreateLabTestOrderWithWallet in labTestPayment.js exactly: resolve
 * the wallet/gateway split server-side → open Checkout ONLY for the
 * shortfall (skipped if wallet covers it all) → complete the checkout
 * intent, which creates the appointment only after the wallet debit +
 * any gateway verification both succeed.
 */
export async function payAndCreateAppointmentWithWallet({
  patientId, doctorId, appointmentType, doctorName, patientName, patientEmail, patientPhone,
  appointmentPayload, countryCode, subscriptionPlanId,
}) {
  // Step 1 — resolve the wallet/gateway split
  let split;
  try {
    const createRes = await paymentAPI.createAppointmentPaymentOrder({
      patientId, doctorId, appointmentType, countryCode, subscriptionPlanId,
      paymentMode: 'WALLET_BALANCE',
    });
    split = createRes.data;
  } catch (err) {
    return {
      success: false,
      reason: 'create_order_failed',
      message: err?.response?.data?.error || err.message || 'Could not start the payment.',
    };
  }

  // Step 2 — open Checkout only if there's a gateway shortfall
  let gatewayPaymentId = null;
  let gatewaySignature = null;

  if (split.gatewayContributionMinorUnits > 0) {
    let checkoutResult;
    try {
      checkoutResult = await openRazorpayCheckout({
        orderId: split.razorpayOrderId,
        amount: split.gatewayContributionMinorUnits,
        currency: split.currency || 'INR',
        name: 'Previu',
        description: doctorName ? `Consultation with ${doctorName}` : 'Doctor consultation',
        prefill: {
          name: patientName || '',
          email: patientEmail || '',
          contact: patientPhone || '',
        },
        themeColor: '#b8843a',
      });
    } catch (err) {
      return { success: false, reason: 'payment_failed', message: err.message };
    }

    if (!checkoutResult) {
      return { success: false, reason: 'payment_cancelled' };
    }
    gatewayPaymentId = checkoutResult.razorpay_payment_id;
    gatewaySignature = checkoutResult.razorpay_signature;
  }

  // Step 3 — complete the checkout intent, THEN create the appointment
  try {
    const res = await paymentAPI.payAndCreateAppointment({
      appointment: appointmentPayload,
      paymentMode: 'WALLET_BALANCE',
      walletCheckoutIntentId: split.walletCheckoutIntentId,
      razorpayPaymentId: gatewayPaymentId,
      razorpaySignature: gatewaySignature,
      countryCode,
      subscriptionPlanId,
    });
    return { success: true, appointment: res.data };
  } catch (err) {
    return {
      success: false,
      reason: 'appointment_creation_failed',
      message: err?.response?.data?.message || err.message ||
        'Payment succeeded but the appointment could not be created. Please contact support — do not pay again.',
    };
  }
}
